<template>
  <CustomModal v-if="open && deal" @close="$emit('close')" class="invest-modal">
    <template v-slot:body>
      <LeftGradientBlockLayout>
        <LeftGradientBlockLayoutHead>
          <template slot="title">{{ $t('modalTitle') }}</template>
        </LeftGradientBlockLayoutHead>
        <LeftGradientBlockLayoutBody>
          <template slot="left">
            <ValueInput v-model.trim="form_data.amount"
                        placeholder="0.00"
                        :label="$t('valueLabel')"
                        :currency_id="currencyId"
                        :class="{'--error':$v.form_data.amount.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.form_data.amount.required">{{ $t('errors.required') }}</div>
              </template>
            </ValueInput>
          </template>
          <template slot="right">
            <BaseTextarea v-model="form_data.note"
                          :label="$t('noteLabel')"
                          class="col-span-2"
                          placeholder=""/>
            <BaseButton theme="red"
                        @click="updateDeal"
                        :loading="submitted">
              {{ $t('buttonLabel') }}
            </BaseButton>
            <div class="flex items-center">
              <VueCtkDateTimePicker id="transaction-date"
                                    class="inline"
                                    v-model="form_data.date"
                                    format="YYYY-MM-DD"
                                    :formatted="dateFormatted"
                                    :locale="$i18n.locale"
                                    :auto-close="true"
                                    :only-date="true"
                                    :no-label="true"
                                    :no-clear-button="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
              <VueCtkDateTimePicker id="transaction-time"
                                    class="inline"
                                    v-model="form_data.time"
                                    format="HH:mm"
                                    formatted="HH:mm"
                                    :locale="$i18n.locale"
                                    :only-time="true"
                                    :no-clear-button="true"
                                    :no-label="true"
                                    :no-button="true"
                                    color="#2D4C5E"/>
            </div>
          </template>
        </LeftGradientBlockLayoutBody>
      </LeftGradientBlockLayout>
    </template>
  </CustomModal>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {decimal, required} from "vuelidate/lib/validators";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import CustomModal from "@/components/UI/modals/CustomModal";
import BaseButton from "@/components/UI/BaseButton";
import BaseTextarea from "@/components/UI/BaseTextarea";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import LeftGradientBlockLayout from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayout";
import LeftGradientBlockLayoutHead
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutHead";
import LeftGradientBlockLayoutBody
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutBody";
import {dealTypes} from "@/utilities/invest_deal_types";

export default {
  name: 'TransferInEditModal',
  components: {
    LeftGradientBlockLayoutBody,
    LeftGradientBlockLayoutHead,
    LeftGradientBlockLayout,
    ValueInput,
    BaseTextarea,
    BaseButton,
    CustomModal,
    BaseSelect,
  },
  props: {
    open: {
      required: true
    },
    deal: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      submitted: false,
      form_data: {
        amount: null,
        note: '',
        date: null,
        time: null,
      },
    }
  },
  computed: {
    ...mapState('investments/portfolios', ['current_portfolio']),
    ...mapGetters('cabinet/currencies', ['getCurrencyByCode']),
    currencyId() {
      if (this.deal && this.deal.instrument && this.deal.instrument.currency) {
        return this.getCurrencyByCode(this.deal.instrument.currency).id;
      }
      return '';
    },
    dateFormatted() {
      const isToday = this.$moment(this.form_data.date, 'YYYY-MM-DD').startOf('day').unix() === this.$moment().startOf('day').unix()

      return isToday ? this.$t('today') : 'DD.MM.YYYY'
    }
  },
  methods: {
    ...mapActions('investments/deals', ['updateDeal']),
    loadDealData() {
      if (!this.deal) return;
      
      const dealDate = this.$moment.unix(this.deal.created_at);
      this.form_data.date = dealDate.format('YYYY-MM-DD');
      this.form_data.time = dealDate.format('HH:mm');
      
      this.form_data.amount = this.deal.amount;
      this.form_data.note = this.deal.note || '';
    },
    updateDeal() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        const data = {
          id: this.deal.id,
          portfolio_id: this.current_portfolio.data.id,
          instrument_id: this.deal.instrument.id,
          type: dealTypes.TYPE_DEPOSITING,
          price: this.deal.price,
          amount: +this.form_data.amount,
          note: this.form_data.note,
          date_time: `${this.form_data.date} ${this.form_data.time}:00`,
          timezone: this.$moment.tz.guess(),
        }
        this.updateDeal(data).then(res => {
          if (res.body.success) {
            this.$toasted.success('Событие успешно обновлено');
            this.$store.dispatch('investments/deals/fetchDealsHistory');
            this.$store.dispatch('investments/portfolios/getPortfolios').then(() => {
              this.$store.dispatch('investments/portfolios/updateCurrentPortfolioInfo')
            })
            this.$emit('close')
            this.$v.$reset();
          } else {
            this.$toasted.error('Ошибка при обновлении события');
          }
          this.submitted = false;
        }).catch(err => {
          this.$toasted.error('Ошибка при обновлении события');
          this.submitted = false;
        })
      }
    },
    resetFields() {
      this.submitted = false;
      this.form_data.amount = null
      this.form_data.note = ''
      this.form_data.date = this.$moment().format('YYYY-MM-DD')
      this.form_data.time = this.$moment().format('HH:mm')
      this.$v.$reset();
    }
  },
  watch: {
    open(val) {
      if (val && this.deal) {
        this.loadDealData();
      } else if (!val) {
        this.resetFields();
      }
    },
    deal: {
      immediate: true,
      handler(val) {
        if (val && this.open) {
          this.loadDealData();
        }
      }
    }
  },
  validations() {
    return {
      form_data: {
        amount: {
          required,
          decimal,
          minValue: 0.01,
        },
      },
    }
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Edit deposit',
        valueLabel: 'Deposit amount',
        noteLabel: 'Note',
        buttonLabel: 'Save',
        today: '[Today]'
      },
      ru: {
        modalTitle: 'Редактировать внесение',
        valueLabel: 'Сумма ввода',
        noteLabel: 'Заметка',
        buttonLabel: 'Сохранить',
        today: 'Сегодня'
      }
    }
  }
}
</script>




